import { useState, useCallback, useMemo, useEffect } from 'react';
import { QueryGroup, QueryRule, QueryField } from '../../types/QueryBuilder';
import { generateId, isRuleValid } from '../../utils/queryBuilderUtils';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';

const SIMPLE_GROUP_ID = 'simple-group';

// Fields whose rule should start pre-filled with a given option instead of empty, matching how a
// native <select> (the previous BeOne UI) always has one option selected — never "nothing chosen".
const DEFAULT_OPTION_NAME_BY_FIELD: Record<string, string> = {
    CUSTOMER_ACCOUNT_TYPE: 'point',
};

const resolveDefaultValue = (field: QueryField): string => {
    const defaultOptionName = DEFAULT_OPTION_NAME_BY_FIELD[field.name]?.toLowerCase();
    if (!defaultOptionName) return '';
    // Match on the technical `name` first, but fall back to the human-readable `label` — the API
    // sometimes exposes "point" only as the display label, with an unrelated code as `name`.
    const match = field.values?.find(v => v.name.toLowerCase() === defaultOptionName)
        ?? field.values?.find(v => v.label.toLowerCase() === defaultOptionName);
    return match?.name ?? '';
};

const createRuleForField = (field: QueryField): QueryRule => ({
    id: generateId(),
    field: field.name,
    operator: (field.defaultOperator as QueryRule['operator']) || '=',
    value: resolveDefaultValue(field),
});

export const useSimpleQueryBuilder = () => {
    const { simpleFields, loadingFields, errorFields } = useQueryBuilderData();

    const [rules, setRules] = useState<QueryRule[]>([]);

    useEffect(() => {
        setRules(prevRules =>
            simpleFields.map(field =>
                prevRules.find(rule => rule.field === field.name) || createRuleForField(field)
            )
        );
    }, [simpleFields]);

    const updateField = useCallback((fieldName: string, data: Partial<QueryRule>) => {
        setRules(prevRules =>
            prevRules.map(rule => (rule.field === fieldName ? { ...rule, ...data } : rule))
        );
    }, []);

    const resetFields = useCallback(() => {
        setRules(simpleFields.map(createRuleForField));
    }, [simpleFields]);

    const group = useMemo<QueryGroup>(() => ({
        id: SIMPLE_GROUP_ID,
        combinator: 'AND',
        rules
    }), [rules]);

    const groups = useMemo(() => [group], [group]);

    const isValid = useMemo(() => rules.some(isRuleValid), [rules]);

    return {
        simpleFields,
        rules,
        group,
        groups,
        isValid,
        updateField,
        resetFields,
        loadingFields,
        fieldsError: errorFields
    };
};
